import { Typography } from '@material-ui/core';
import { AttachFile, Cancel, CheckCircle } from '@material-ui/icons';
import { errorMessages } from 'common/errorMessages.strings';
import { tipoDocumentoS3Descriptions } from 'common/genericDescriptions';
import { CadastroNCByFile, TipoDocumentoS3, FuncaoParticipante, GrupoAcesso, Perfil } from 'common/genericTypes';
import { Button, MdSpace, SmSpace } from 'm2m.components';
import React, { Fragment, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DataGridPro, GridCellParams, GridSortDirection, GridSortModel, GridColDef } from '@mui/x-data-grid-pro';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { sanitizeAlphanumericCnpj } from 'utils/cnpjUtils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import _ from 'lodash';
import { addAlert } from 'store/modules/snackBar';
import { FileService } from 'services/fileService';
import { Attachment as AttachmentType, FundosInvestidoresEmLoteProps, FundoInvestidorParsedData, defaultAcceptableFileTypes } from './fundosInvestidoresEmLoteTypes';
import { useHistory } from 'react-router-dom';

// Constantes
const TEMPLATE_HEADERS = [
    'Nome Fantasia', 'CNPJ do Fundo', 'Razão Social do Administrador', 'CNPJ do Administrador',
    'Razão Social do Gestor', 'CNPJ do Gestor', 'CNPJ do Escriturador', 'CNPJ do Custodiante',
    'Razão Social do Banco Liquidante', 'CNPJ do Banco Liquidante', 'ISPB do Banco Liquidante',
    'Agência', 'Conta Movimento', 'Código do Ativo', 'Classe', 'Classificação ANBIMA',
    'Código ANBIMA', 'Fundo Exclusivo', 'Tipo do Fundo'
];

const HEADER_MAP: { [key: string]: string } = {
    'Nome Fantasia': 'nomeFantasia',
    'CNPJ do Fundo': 'cnpjFundo',
    'Razão Social do Administrador': 'razaoSocialAdministrador',
    'CNPJ do Administrador': 'cnpjAdministrador',
    'Razão Social do Gestor': 'razaoSocialGestor',
    'CNPJ do Gestor': 'cnpjGestor',
    'CNPJ do Escriturador': 'cnpjEscriturador',
    'CNPJ do Custodiante': 'cnpjCustodiante',
    'Razão Social do Banco Liquidante': 'razaoSocialBancoLiquidante',
    'CNPJ do Banco Liquidante': 'cnpjBancoLiquidante',
    'ISPB do Banco Liquidante': 'ispbBancoLiquidante',
    'Agência': 'agencia',
    'Conta Movimento': 'contaMovimento',
    'Código do Ativo': 'codigoAtivo',
    'Classe': 'classe',
    'Classificação ANBIMA': 'classificacaoAnbima',
    'Código ANBIMA': 'codigoAnbima',
    'Fundo Exclusivo': 'fundoExclusivo',
    'Tipo do Fundo': 'tipoFundo'
};

const OPTIONAL_HEADERS = ['Agência', 'Conta Movimento', 'Classe'];

const STATUS_COLORS = {
    FAILED: '#f44336',
    SUCCESS: '#4caf50',
    READY: '#2196f3',
    VALIDATION_ERROR: '#ff9800'
};

const ROW_STYLES = {
    failed: {
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.15)' }
    },
    success: {
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.15)' }
    },
    validationError: {
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.15)' }
    }
};

type FundoStatus = 'failed' | 'success' | 'ready' | 'validation-error';

interface StatusInfo {
    color: string;
    icon: React.ReactElement;
    text: string;
}

enum ProcessingResult {
    SUCCESS_TOTAL = 'SUCCESS_TOTAL',
    SUCCESS_PARTIAL = 'SUCCESS_PARTIAL',
    FAILURE_TOTAL = 'FAILURE_TOTAL',
    ERROR_GENERIC = 'ERROR_GENERIC'
}

interface ProcessingOutcome {
    result: ProcessingResult;
    shouldSaveFile: boolean;
    message: string;
    severity: 'success' | 'warning' | 'error';
    shouldRedirect: boolean;
}

const ValidationUtils = {
    cnpj: (cnpj: string): boolean => {
        if (!cnpj) return false;
        // Remove formatação mantendo letras e dígitos
        const sanitized = sanitizeAlphanumericCnpj(cnpj).trim();
        if (sanitized.length !== 14) return false;

        // Verifica se os 2 últimos caracteres são numéricos (dígitos verificadores)
        const digitosVerificadores = sanitized.substring(12);
        if (!/^\d{2}$/.test(digitosVerificadores)) return false;

        // Verifica se os 12 primeiros são alfanuméricos
        const base = sanitized.substring(0, 12);
        if (!/^[A-Z0-9]{12}$/.test(base)) return false;

        // Rejeita CNPJs com todos os caracteres iguais
        if (/^(.)\1{13}$/.test(sanitized)) return false;

        const multiplicador1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        const multiplicador2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

        // Valor numérico: dígitos = 0-9, letras = ASCII - 48
        const getCharValue = (char: string): number => char.charCodeAt(0) - 48;

        let soma = 0;
        for (let i = 0; i < 12; i++) {
            soma += getCharValue(base[i]) * multiplicador1[i];
        }

        let resto = soma % 11;
        resto = resto < 2 ? 0 : 11 - resto;
        const digito1 = resto;
        if (digito1 !== parseInt(digitosVerificadores[0])) return false;

        const baseComDigito1 = base + digito1.toString();
        soma = 0;
        for (let i = 0; i < 13; i++) {
            soma += getCharValue(baseComDigito1[i]) * multiplicador2[i];
        }

        resto = soma % 11;
        resto = resto < 2 ? 0 : 11 - resto;
        const digito2 = resto;
        if (digito2 !== parseInt(digitosVerificadores[1])) return false;

        return true;
    },
    
    ispb: (ispb: string): boolean => {
        if (!ispb) return false;
        const cleaned = ispb.replace(/\D/g, '');
        return cleaned.length === 8;
    },
    
    padCnpj: (value: string): string => {
        // Remove formatação mantendo letras e dígitos
        const clean = sanitizeAlphanumericCnpj(value);
        // Se contém letras, é alfanumérico - padding com '0' à esquerda
        return clean.length <= 14 ? clean.padStart(14, '0') : clean;
    }
};

const ProcessingUtils = {
    analyzeProcessingResult: (
        totalRecords: number,
        failedRecords: any[],
        formattedData: any[],
        entityType: 'fundo' | 'investidor'
    ): ProcessingOutcome => {
        const hasSpecificRecordErrors = failedRecords.some(f => 
            f?.fundoInvestidor?.cnpjFundo || f?.fundo?.cnpjFundo || f?.investidor?.cpfCnpj ||
            (f?.error && ProcessingUtils.extractCnpjFromError(f.error))
        );

        if (!hasSpecificRecordErrors && failedRecords.length > 0) {
            const errorMessage = failedRecords[0]?.error || 'Erro no cadastro dos registros';
            const entityName = entityType === 'fundo' ? 'fundo(s) investidor(es)' : 'investidor(es)';
            return {
                result: ProcessingResult.FAILURE_TOTAL,
                shouldSaveFile: false,
                message: `Falha ao inserir todos os ${totalRecords} ${entityName}. ${errorMessage}`,
                severity: 'error',
                shouldRedirect: false
            };
        }

        const failedCnpjsMap = new Map<string, { error: string, count: number }>();
        failedRecords.forEach(failed => {
            let cnpj = entityType === 'fundo' 
                ? (failed?.fundoInvestidor?.cnpjFundo || failed?.fundo?.cnpjFundo)
                : failed?.investidor?.cpfCnpj;
            
            if (!cnpj && failed?.error) {
                cnpj = ProcessingUtils.extractCnpjFromError(failed.error);
            }
            
            if (cnpj) {
                if (failedCnpjsMap.has(cnpj)) {
                    const existing = failedCnpjsMap.get(cnpj)!;
                    failedCnpjsMap.set(cnpj, { ...existing, count: existing.count + 1 });
                } else {
                    failedCnpjsMap.set(cnpj, { error: failed.error, count: 1 });
                }
            }
        });

        const cnpjCountInFile = new Map<string, number>();
        formattedData.forEach(record => {
            const cnpj = entityType === 'fundo' ? record.cnpjFundo : record.cpfCnpj;
            if (cnpj) {
                cnpjCountInFile.set(cnpj, (cnpjCountInFile.get(cnpj) || 0) + 1);
            }
        });

        let actualFailedRecords = 0;
        failedCnpjsMap.forEach((failedInfo, cnpj) => {
            const totalOccurrencesInFile = cnpjCountInFile.get(cnpj) || 0;
            const failedOccurrences = failedInfo.count;
            
            if (failedOccurrences >= totalOccurrencesInFile) {
                actualFailedRecords += totalOccurrencesInFile;
            } else {
                actualFailedRecords += failedOccurrences;
            }
        });

        const successRecords = totalRecords - actualFailedRecords;
        const entityName = entityType === 'fundo' ? 'fundo(s) investidor(es)' : 'investidor(es)';
        
        if (actualFailedRecords === 0 && successRecords > 0) {
            return {
                result: ProcessingResult.SUCCESS_TOTAL,
                shouldSaveFile: true,
                message: `Arquivo processado com sucesso: ${successRecords} ${entityName} cadastrado(s).`,
                severity: 'success',
                shouldRedirect: true
            };
        }
        
        if (actualFailedRecords > 0 && successRecords > 0) {
            return {
                result: ProcessingResult.SUCCESS_PARTIAL,
                shouldSaveFile: true,
                message: `Arquivo processado parcialmente: ${successRecords} ${entityName} cadastrado(s) com sucesso, ${actualFailedRecords} com erro.`,
                severity: 'warning',
                shouldRedirect: false
            };
        }
        
        if (actualFailedRecords === totalRecords) {
            return {
                result: ProcessingResult.FAILURE_TOTAL,
                shouldSaveFile: false,
                message: `Falha ao inserir todos os ${actualFailedRecords} ${entityName}. Nenhum registro foi cadastrado.`,
                severity: 'error',
                shouldRedirect: false
            };
        }
        
        return {
            result: ProcessingResult.ERROR_GENERIC,
            shouldSaveFile: false,
            message: 'Falha ao enviar arquivo.',
            severity: 'error',
            shouldRedirect: false
        };
    },

    processGridDataWithDuplicates: (
        formattedData: any[],
        failedRecords: any[],
        entityType: 'fundo' | 'investidor'
    ) => {
        if (failedRecords.length === 0) {
            return formattedData;
        }

        const failedIdsMap = new Map<string, { error: string, count: number }>();
        failedRecords.forEach(failed => {
            let id = entityType === 'fundo' 
                ? (failed?.fundoInvestidor?.cnpjFundo || failed?.fundo?.cnpjFundo)
                : failed?.investidor?.cpfCnpj;
            
            if (!id && failed?.error) {
                id = ProcessingUtils.extractCnpjFromError(failed.error);
            }
            
            if (id) {
                if (failedIdsMap.has(id)) {
                    const existing = failedIdsMap.get(id)!;
                    failedIdsMap.set(id, { ...existing, count: existing.count + 1 });
                } else {
                    failedIdsMap.set(id, { error: failed.error, count: 1 });
                }
            }
        });

        const idCountInFile = new Map<string, number>();
        formattedData.forEach(record => {
            const id = entityType === 'fundo' ? record.cnpjFundo : record.cpfCnpj;
            if (id) {
                idCountInFile.set(id, (idCountInFile.get(id) || 0) + 1);
            }
        });

        return formattedData.map((record, index) => {
            const id = entityType === 'fundo' ? record.cnpjFundo : record.cpfCnpj;
            const failedInfo = failedIdsMap.get(id);
            
            if (failedInfo) {
                const totalOccurrencesInFile = idCountInFile.get(id) || 1;
                const failedOccurrences = failedInfo.count;
                const isFirstOccurrence = formattedData.findIndex(f => 
                    entityType === 'fundo' ? f.cnpjFundo === id : f.cpfCnpj === id
                ) === index;
                const shouldMarkAsFailed = failedOccurrences >= totalOccurrencesInFile || !isFirstOccurrence;
                
                if (shouldMarkAsFailed) {
                    const errorMessage = StatusUtils.processErrorMessage(
                        failedInfo.error || 'Falha ao inserir no sistema.',
                        id
                    );
                    
                    return {
                        ...record,
                        valido: false,
                        mensagemErro: errorMessage,
                        failedInBackend: true
                    };
                } else {
                    return {
                        ...record,
                        successfullyRegistered: true
                    };
                }
            }

            if (record.valido) {
                return {
                    ...record,
                    successfullyRegistered: true
                };
            }

            return record;
        });
    },

    extractCnpjFromError: (errorMessage: string): string | null => {
        const fundoMatch = errorMessage.match(/Fundo:\s*(\d{14})/);
        if (fundoMatch) {
            return fundoMatch[1];
        }
        
        const cnpjMatch = errorMessage.match(/(\d{14})/);
        if (cnpjMatch) {
            return cnpjMatch[1];
        }
        
        return null;
    }
};

const StatusUtils = {
    getStatusInfo: (row: any): StatusInfo => {
        if (row.failedInBackend) {
            return {
                color: STATUS_COLORS.FAILED,
                icon: <Cancel fontSize="small" style={{ marginRight: '5px' }} />,
                text: 'Falha ao cadastrar'
            };
        }
        if (row.successfullyRegistered) {
            return {
                color: STATUS_COLORS.SUCCESS,
                icon: <CheckCircle fontSize="small" style={{ marginRight: '5px' }} />,
                text: 'Cadastrado com sucesso'
            };
        }
        if (row.valido) {
            return {
                color: STATUS_COLORS.READY,
                icon: <CheckCircle fontSize="small" style={{ marginRight: '5px' }} />,
                text: 'Pronto para envio'
            };
        }
        return {
            color: STATUS_COLORS.VALIDATION_ERROR,
            icon: <Cancel fontSize="small" style={{ marginRight: '5px' }} />,
            text: 'Erro de validação'
        };
    },
    
    getRowClassName: (row: any): string => {
        if (row.failedInBackend) return 'failed-row';
        if (row.successfullyRegistered) return 'success-row';
        if (!row.valido) return 'validation-error-row';
        return '';
    },
    
    processErrorMessage: (error: string, cnpj?: string): string => {
        const duplicateErrors = ['já cadastrado', 'already exists', 'duplicat', 'CPF/CNPJ já cadastrado'];
        const invalidErrors = ['invalid', 'inválido'];
        
        if (error.includes('Fundo:') && error.includes(' - ')) {
            const cleanedError = error.replace(/^Fundo:\s*\d{14}\s*-\s*/, '');
            return cleanedError;
        }
        
        if (duplicateErrors.some(err => error.includes(err) || error === err)) {
            return `Fundo com CNPJ ${cnpj || 'informado'} já está cadastrado no sistema.`;
        }
        if (invalidErrors.some(err => error.includes(err))) {
            return `Dados inválidos para o fundo com CNPJ ${cnpj || 'informado'}.`;
        }
        return error;
    }
};

const FundosInvestidoresEmLote: React.FC<FundosInvestidoresEmLoteProps> = (props) => {
    const { overrideAcceptableFileTypes } = props;
    const history = useHistory();
    const [highlight, setHightlight] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [attachments, setAttachments] = useState<Array<AttachmentType>>([]);
    const [file, setFile] = useState<File | undefined>();
    const [formattedData, setFormattedData] = useState<Array<FundoInvestidorParsedData>>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [pageSize, setPageSize] = React.useState(20);
    const [failedFundos, setFailedFundos] = useState<{ fundo?: any, fundoInvestidor?: any, error: string }[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);

    const isUsuarioMaster = useSelector((state: RootState) => state.auth.usuario.participante.perfil) === Perfil.Master;
    const isUsuarioOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
    const isUsuarioCadastroOperacoes = useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes;
    const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil) === Perfil.Leitura;
    const isUsuarioEscrita = useSelector((state: RootState) => state.auth.usuario.participante.perfil) === Perfil.Escrita;
    const isParticipanteCustodiante = useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Custodiante;
    const participante = useSelector((state: RootState) => state.auth.usuario.participante);
    const notaComercialByFileApi = useSelector((state: RootState) => state.notaComercialByFileApi);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'dataResponse',
            sort: 'desc' as GridSortDirection,
        },
    ]);


    const dispatch = useDispatch();

    const acceptedFileTypes = !!overrideAcceptableFileTypes?.length
        ? overrideAcceptableFileTypes
        : defaultAcceptableFileTypes;

    const isValidFileType = (files: File[]): boolean => {
        return acceptedFileTypes.some((type) => files.some((file) => file?.type === type));
    };

    const handleFileTypeError = () => {
        props.onError?.(errorMessages.validation.upload.invalidExtension);
        setHightlight(false);
    };

    const openFileDialog = () => {
        if (props.disabled) return;
        inputRef?.current?.click();
    };

    const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setFile(undefined);
        setAttachments([]);
        setFormattedData([]);
        setValidationErrors([]);
        setFailedFundos([]);
        if (props.disabled) return;
        let newFiles: File[] = [];
        if (props.multipleFiles) {
            newFiles = Array.from(event.dataTransfer.files);
        } else if (event.dataTransfer.files) {
            newFiles[0] = event.dataTransfer.files[0];
        }

        if (!newFiles.length) return;

        if (!isValidFileType(newFiles)) {
            handleFileTypeError();
            return;
        }

        const newAttachments = newFiles.map((newFile) => {
            return {
                id: props.fileId ?? uuid(),
                file: newFile,
            };
        });
        setAttachments(newAttachments);
        setFile(event.dataTransfer.files[0]);
        setHightlight(false);

        if (newFiles[0]) {
            try {
                await parseAndValidateFile(newFiles[0]);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Erro ao processar arquivo';
                dispatch(addAlert({ severity: 'error', message: errorMessage }));
                props.onError?.(error);
            }
        }
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (props.disabled) return;
        setHightlight(true);
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (props.disabled) return;
        setHightlight(false);
    };

    const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setFile(undefined);
        setAttachments([]);
        setFormattedData([]);
        setValidationErrors([]);
        setFailedFundos([]);
        let newFiles: File[] = [];
        if (event.target.files) {
            newFiles[0] = event.target.files?.[0];
        }

        if (!newFiles.length) return;

        if (!isValidFileType(newFiles)) {
            handleFileTypeError();
            return;
        }

        const newAttachments = newFiles.map((newFile) => {
            return {
                id: props.fileId ?? uuid(),
                file: newFile,
            };
        });

        let updatedAttachments: AttachmentType[];
        if (props.fileId && attachments.some(({ id }) => id === props.fileId)) {
            updatedAttachments = attachments.filter((file) => file.id !== props.fileId).concat(newAttachments);
        } else {
            updatedAttachments = attachments.concat(newAttachments);
        }

        props.onChange?.(newAttachments);
        setHightlight(false);
        setAttachments(newAttachments);
        setFile(event.target.files?.[0]);

        if (newFiles[0]) {
            try {
                await parseAndValidateFile(newFiles[0]);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Erro ao processar arquivo';
                dispatch(addAlert({ severity: 'error', message: errorMessage }));
                props.onError?.(error);
            }
        }
    };

    const parseAndValidateFile = async (file: File) => {
        try {
            setIsValidating(true);
            setLoading(true);

            const parsedData = await FileService.parseFile(file, TEMPLATE_HEADERS);

            setHeaders(parsedData.headers);

            const fundos: FundoInvestidorParsedData[] = parsedData.rows.map((row) => {
                const fundo: any = {
                    id: uuid()
                };

                parsedData.headers.forEach((header, headerIndex) => {
                    const fieldName = HEADER_MAP[header] ?? _.camelCase(header);
                    const cellValue = row[headerIndex];
                    fundo[fieldName] = cellValue !== null && cellValue !== undefined ? String(cellValue) : '';
                    
                    if (header.includes('CNPJ') && cellValue) {
                        fundo[fieldName] = ValidationUtils.padCnpj(cellValue);
                    }
                });

                const errors: string[] = [];

                parsedData.headers.forEach((header) => {
                    if (OPTIONAL_HEADERS.includes(header)) return;
                    const fieldName = HEADER_MAP[header] ?? _.camelCase(header);
                    if (!fundo[fieldName]) {
                        errors.push(`${header} é obrigatório`);
                    }
                });

                // Validações de CNPJ
                const cnpjFields = [
                    { field: 'cnpjFundo', label: 'CNPJ do Fundo' },
                    { field: 'cnpjAdministrador', label: 'CNPJ do Administrador' },
                    { field: 'cnpjGestor', label: 'CNPJ do Gestor' },
                    { field: 'cnpjEscriturador', label: 'CNPJ do Escriturador' },
                    { field: 'cnpjCustodiante', label: 'CNPJ do Custodiante' },
                    { field: 'cnpjBancoLiquidante', label: 'CNPJ do Banco Liquidante' }
                ];

                cnpjFields.forEach(({ field, label }) => {
                    if (fundo[field] && !ValidationUtils.cnpj(fundo[field])) {
                        errors.push(`${label} inválido`);
                    }
                });

                if (fundo.ispbBancoLiquidante && !ValidationUtils.ispb(fundo.ispbBancoLiquidante)) {
                    errors.push('ISPB do Banco Liquidante inválido');
                }

                fundo.valido = errors.length === 0;
                fundo.mensagemErro = errors.join(', ');

                return fundo as FundoInvestidorParsedData;
            });

            setFormattedData(fundos);

            const invalidRecords = fundos.filter(fundo => !fundo.valido);
            if (invalidRecords.length > 0) {
                setValidationErrors([`${invalidRecords.length} registro(s) com erro de validação.`]);
                dispatch(addAlert({
                    severity: 'warning',
                    message: `Arquivo processado com ${invalidRecords.length} erro(s) de validação. Verifique os detalhes na tabela.`
                }));
            } else if (fundos.length === 0) {
                setValidationErrors(['Arquivo processado sem registros válidos.']);
                dispatch(addAlert({
                    severity: 'warning',
                    message: 'Arquivo processado sem registros válidos.'
                }));
            } else {
                dispatch(addAlert({
                    severity: 'success',
                    message: `Arquivo processado com sucesso! ${fundos.length} registros válidos.`
                }));
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Erro ao processar arquivo';
            setValidationErrors([errorMessage]);
            throw error;
        } finally {
            setIsValidating(false);
            setLoading(false);
        }
    };

    const processGridData = useCallback((fundos: FundoInvestidorParsedData[]) => {
        return ProcessingUtils.processGridDataWithDuplicates(fundos, failedFundos, 'fundo');
    }, [failedFundos]);

    const getBase64 = async (file: File) => {
        let base64File;
        var reader = new FileReader();
        reader.readAsDataURL(file);
        await new Promise((resolve) => {
            reader.onload = () => {
                base64File = reader.result;
                resolve(base64File);
            };
        });
        return reader;
    };

    const uploadFile = async (file: File | undefined) => {
        if (!file) {
            dispatch(addAlert({ severity: 'error', message: 'Nenhum arquivo selecionado.' }));
            return;
        }

        const invalidRecords = formattedData.filter(fundo => !fundo.valido);
        if (invalidRecords.length > 0) {
            dispatch(addAlert({
                severity: 'error',
                message: 'Não é possível enviar o arquivo com erros de validação. Corrija os erros e tente novamente.'
            }));
            return;
        }

        let shouldSaveFile = false;
        let cadastroMessage = '';
        
        try {
            setLoading(true);
            await notaComercialByFileApi.postFundosInvestidoresLoteByFile(dispatch, file, true);
            
            shouldSaveFile = true;
            cadastroMessage = 'Arquivo enviado com sucesso!';
            dispatch(addAlert({ severity: 'success', message: cadastroMessage }));
            history.push('/gerenciar/investidores');
            
        } catch (error: any) {
            if (error.response?.data) {
                if (typeof error.response.data === 'string') {
                    dispatch(addAlert({ severity: 'error', message: error.response.data }));
                    return;
                }

                const failed: { fundo?: any, fundoInvestidor?: any, error: string }[] = error.response.data || [];
                setFailedFundos(failed);

                const outcome = ProcessingUtils.analyzeProcessingResult(
                    formattedData.length,
                    failed,
                    formattedData,
                    'fundo'
                );

                shouldSaveFile = outcome.shouldSaveFile;
                cadastroMessage = outcome.message;
                
                dispatch(addAlert({ 
                    severity: outcome.severity, 
                    message: outcome.message 
                }));

                if (outcome.shouldRedirect) {
                    history.push('/gerenciar/investidores');
                }
            } else {
                dispatch(addAlert({ severity: 'error', message: 'Falha ao enviar arquivo.' }));
            }
        } finally {
            setLoading(false);
        }
        if (shouldSaveFile) {
            try {
                const file2Base64: string = (await getBase64(file)).result as string;
                
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');
                const timestamp = `${year}_${month}_${day}_${hours}${minutes}${seconds}`;
                const fileName = file?.name ?? '';
                const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
                const fileNameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
                const fileNameWithTimestamp = `${fileNameWithoutExtension}_${timestamp}${fileExtension}`;
                
                const notaComercial: CadastroNCByFile = {
                    diretorio: `${participante.id}/request/${tipoDocumentoS3Descriptions[TipoDocumentoS3.CadastroFundosLote]}`,
                    nomeArquivo: fileNameWithTimestamp,
                    arquivo: file2Base64.split(',')[1],
                };
                await notaComercialByFileApi.postNotaComercialByFile(dispatch, notaComercial, true);
            } catch (saveError) {
                console.warn('Erro ao salvar arquivo, mas cadastro foi realizado:', saveError);
            }
        }
    };

    const clearFields = () => {
        setFile(undefined);
        setAttachments([]);
        setFormattedData([]);
        setValidationErrors([]);
        setFailedFundos([]);
        setHeaders([]);
        
        // Limpar o input de arquivo
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        
        dispatch(addAlert({ 
            severity: 'info', 
            message: 'Arquivo limpo com sucesso!' 
        }));
    };

    const getDynamicGridColumns = useCallback((): GridColDef[] => {
        const statusColumn: GridColDef = {
            field: 'status',
            headerName: 'Status',
            filterable: true,
            sortable: true,
            renderCell: (params: GridCellParams) => {
                const statusInfo = StatusUtils.getStatusInfo(params.row);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', color: statusInfo.color }}>
                        {statusInfo.icon}
                        <span>{statusInfo.text}</span>
                    </div>
                );
            },
            width: 200,
            minWidth: 150,
            maxWidth: 250,
        };

        const detailsColumn: GridColDef = {
            field: 'mensagemErro',
            headerName: 'Detalhes',
            filterable: true,
            sortable: true,
            renderCell: (params: GridCellParams) => {
                if (!params.row.mensagemErro) return null;
                return (
                    <div style={{ 
                        whiteSpace: 'normal', 
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        padding: '4px 0'
                    }}>
                        {params.row.mensagemErro}
                    </div>
                );
            },
            width: 350,
            minWidth: 250,
            flex: 1,
        };

        const dataColumns = headers.map<GridColDef>(header => ({
            field: HEADER_MAP[header] ?? _.camelCase(header),
            headerName: header,
            filterable: true,
            sortable: true,
            width: 200,
            minWidth: 100,
            flex: 1,
        }));

        return [statusColumn, detailsColumn, ...dataColumns];
    }, [headers]);

    const hideCondition =
        (isUsuarioOperacoes && isUsuarioMaster) ||
        (isUsuarioOperacoes && isUsuarioLeitura) ||
        (isParticipanteCustodiante && isUsuarioLeitura) ||
        (isUsuarioCadastroOperacoes && isUsuarioLeitura);

    const showCondition =
        (isUsuarioOperacoes && isUsuarioEscrita) ||
        (isParticipanteCustodiante && isUsuarioEscrita) ||
        (isParticipanteCustodiante && isUsuarioMaster) ||
        (isUsuarioCadastroOperacoes && isUsuarioEscrita) ||
        (isUsuarioCadastroOperacoes && isUsuarioMaster);

    return (
        <Fragment>
            {showCondition && !hideCondition && (
                <>
                    <SmSpace></SmSpace>
                    <div
                        onClick={openFileDialog}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        style={{
                            width: '100%',
                            border: '1px dashed #D7D8D9',
                            borderRadius: '8px',
                            cursor: props.disabled ? 'default' : 'pointer',
                            textAlign: 'center',
                        }}
                    >
                        <MdSpace />
                        <AttachFile fontSize="small" />
                        <Typography color="textSecondary" variant="body2">
                            {highlight
                                ? 'Solte para carregar os arquivos'
                                : `Arraste e solte ou SELECIONE OS DOCUMENTOS`}
                        </Typography>
                        <input
                            ref={inputRef}
                            type="file"
                            accept={acceptedFileTypes.join(',')}
                            multiple={false}
                            style={{ display: 'none' }}
                            onChange={onInputChange}
                        />
                        <MdSpace />
                    </div>

                    <MdSpace />
                </>
            )}

            <DataGridPro
                autoHeight
                localeText={localizedTextsMap}
                componentsProps={componentsPropsValues}
                rows={processGridData(formattedData)}
                columns={getDynamicGridColumns()}
                components={{
                    Toolbar: CustomToolBarUtils,
                }}
                disableSelectionOnClick
                rowsPerPageOptions={rowsPerPageOptionsValues}
                pagination
                onPageSizeChange={(newPage) => setPageSize(newPage)}
                pageSize={pageSize}
                loading={loading || isValidating}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                getRowClassName={(params) => StatusUtils.getRowClassName(params.row)}
                sx={{
                    '& .failed-row': ROW_STYLES.failed,
                    '& .success-row': ROW_STYLES.success,
                    '& .validation-error-row': ROW_STYLES.validationError,
                }}
            />

            <MdSpace />

            <SmSpace />

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    size="small"
                    color='primary'
                    disabled={isValidating || loading || !file || formattedData.length === 0 || validationErrors.length > 0}
                    onClick={() => uploadFile(file)}
                >
                    {loading ? 'Enviando...' : 'Enviar Arquivo'}
                </Button>

                <Button
                    variant="contained"
                    size="small"
                    color='primary'
                    disabled={isValidating || loading || (formattedData.length === 0 && !file && attachments.length === 0)}
                    onClick={clearFields}
                >
                    Limpar Arquivo
                </Button>

                {validationErrors.length > 0 && (
                    <Typography color="error" variant="body2">
                        {validationErrors.join(', ')}
                    </Typography>
                )}
            </div>

            <MdSpace />

            <SmSpace />
        </Fragment>
    );
};

export default FundosInvestidoresEmLote;
